import styled, { css } from 'styled-components';
import { ifNotProp, ifProp, palette } from 'styled-tools';

import { fontSize, FontSizes, fontWeight, FontWeights, minWidth, units } from '@calm-web/design-system';

import {
	landingGridWrapperMobileWidth,
	landingGridWrapperTabletWidth,
} from '@/components/b2b/Landing/LegacyLandingDisplay/styles';
import Image from '@/components/image/NextImage';

import { b2bRedemptionRevampContentMaxWidth } from '../utils';

const heroWrapperMobilePaddingLeft = units(1.25);

export const HeroWrapper = styled.header<{
	compact: boolean;
	disableGradient: boolean;
}>`
	height: ${units(20)};
	background-size: unset;
	background-image: unset;
	display: flex;
	align-items: center;
	justify-content: left;
	width: ${landingGridWrapperMobileWidth};
	padding-left: ${heroWrapperMobilePaddingLeft};

	${minWidth('tablet')} {
		width: ${landingGridWrapperTabletWidth};
		height: ${units(20)};
	}

	${minWidth('desktop')} {
		display: block;
		height: unset;
		min-height: 100vh;
		margin: unset;
		padding: unset;
		width: unset;
	}
`;

export const ContentWrapper = styled.div`
	color: ${palette('white')};
	display: flex;
	flex-direction: column;
	width: 100%;
`;

export const HeroText = styled.h1`
	font-style: normal;
	${fontWeight(FontWeights.Light)};
	font-size: 28px;
	line-height: 42px;
	margin: 0 auto 16px auto;
	display: none;

	${minWidth('desktop')} {
		display: block;
		color: ${palette('blackAlpha80')};
		${fontSize(FontSizes['2xl'])}
		line-height: 150%;
		margin: unset;
		margin-top: ${units(14.22)};
		max-width: 380px;
	}
`;

export const SubHeroText = styled.div`
	${fontWeight(FontWeights.Light)};
	font-size: 10px;
	margin: 16px auto 0px auto;
	max-width: 300px;

	${minWidth('tablet')} {
		max-width: 480px;
		font-size: 18px;
	}

	${minWidth('desktop')} {
		max-width: 420px;
		margin: 32px auto 0px auto;
	}
`;

export const RevampLogoWrapper = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;
	padding: 24px 16px 16px 16px;
	max-width: ${b2bRedemptionRevampContentMaxWidth};
	margin: auto;

	${minWidth('desktop')} {
		padding: 20px 20px 40px 20px;
		justify-content: left;
	}
`;

export const LogoWrapper = styled.div`
	display: flex;
	align-items: center;
	width: 100%;

	${minWidth('desktop')} {
		max-width: 1440px;
		margin: unset;
		margin-top: ${units(4)};
	}
`;

const logoHorizontalMarginDesktop = units(2.22);

export const LogoDivider = styled.div<{ isRevampRedempFlow?: boolean }>`
	${ifProp(
		'isRevampRedempFlow',
		css`
			background: ${palette('gray3')};
			height: ${units(7.5)};
		`,
	)}

	${ifNotProp(
		'isRevampRedempFlow',
		css`
			${minWidth('tablet')} {
				height: ${units(10)};
			}

			${minWidth('desktop')} {
				height: ${units(11.11)};
			}
		`,
	)}
	background: ${palette('blackAlpha80')};
	margin: 0 ${units(2.5)};
	width: 1px;
	height: ${units(10)};

	${minWidth('desktop')} {
		margin: 0 ${logoHorizontalMarginDesktop};
	}
`;

export const PartnerLogo = styled(Image)``;

export const CalmLogo = styled(Image)``;

const logoHeightRevamp = units(7.5);
const logoHeightMobile = units(7.5);
const logoHeightDesktop = units(8.89);
const logoHeights = css`
	height: ${logoHeightMobile};

	${minWidth('desktop')} {
		height: ${logoHeightDesktop};
	}
`;

export const CalmLogoImageRevampContainer = styled.div<{
	isOnlyCalmLogo?: boolean;
	isEws?: boolean;
}>`
	position: relative;
	height: ${logoHeightRevamp};
	width: ${logoHeightRevamp};

	${ifProp(
		'isOnlyCalmLogo',
		css`
			margin: auto;

			${minWidth('desktop')} {
				margin: unset;
			}
		`,
	)}

	${ifNotProp(
		'isOnlyCalmLogo',
		css`
			${ifNotProp('isEws', 'width: 300px;')}
			flex-shrink: 1;

			${minWidth('desktop')} {
				width: ${logoHeightRevamp};
				flex-shrink: unset;
			}
		`,
	)}
`;

export const CalmLogoImageContainer = styled.div`
	position: relative;
	${logoHeights};

	width: ${logoHeightMobile};

	${minWidth('desktop')} {
		width: ${logoHeightDesktop};
	}
`;

export const ImageContainer = styled.div<{ isRevampRedempFlow?: boolean }>`
	position: relative;
	max-width: 300px;
	flex-grow: 1;
	height: ${logoHeightMobile};

	${ifNotProp(
		'isRevampRedempFlow',
		css`
			margin-right: ${heroWrapperMobilePaddingLeft};

			${minWidth('desktop')} {
				height: ${logoHeightDesktop};
			}
		`,
	)}

	${ifProp(
		'isRevampRedempFlow',
		css`
			max-width: unset;
			width: 300px;
			flex-shrink: 1;
			flex-grow: unset;
		`,
	)}

	${minWidth('desktop')} {
		margin-right: ${logoHorizontalMarginDesktop};
	}
`;

export const PartnerName = styled.div<{ isRevampRedempFlow?: boolean }>`
	font-size: ${units(2.25)};
	max-width: 300px;
	line-height: 150%;
	color: ${palette('blackAlpha80')};
	${fontWeight(FontWeights.Regular)}

	${ifProp(
		'isRevampRedempFlow',
		css`
			width: 300px;
		`,
	)}

	${minWidth('tablet')} {
		font-size: ${units(2.25)};
	}

	${minWidth('desktop')} {
		font-size: ${units(3)};
	}
`;

export const Container = styled.div`
	max-height: unset !important;
	display: flex;
	justify-content: center;
	margin: 0 ${units(1.25)};

	${minWidth('desktop')} {
		display: block;
		width: unset;
		margin: unset;
		flex: 0 0 520px;
	}
`;
